import { useClaimStatus } from '@/hooks'
import { PbWfpClaimStatusEnum } from '@itsrever/rever-api'
import {
  Base,
  ShieldAlertIcon,
  ShieldCheckIcon,
  ShieldCrossIcon
} from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

type Tone = 'warning' | 'success' | 'danger' | 'neutral'

interface StatusConfig {
  tone: Tone
  Icon: React.FC
  titleKey: string
  descriptionKey: string
}

const STATUS_CONFIG: Record<PbWfpClaimStatusEnum, StatusConfig> = {
  WFP_CLAIM_STATUS_UNSPECIFIED: {
    tone: 'neutral',
    Icon: ShieldCrossIcon,
    titleKey: 'process_details_page.claim_banner.unspecified.title',
    descriptionKey: 'process_details_page.claim_banner.unspecified.description'
  },
  WFP_CLAIM_STATUS_PENDING_SUBMISSION: {
    tone: 'warning',
    Icon: ShieldAlertIcon,
    titleKey: 'process_details_page.claim_banner.pending_submission.title',
    descriptionKey:
      'process_details_page.claim_banner.pending_submission.description'
  },
  WFP_CLAIM_STATUS_CREATED: {
    tone: 'warning',
    Icon: ShieldAlertIcon,
    titleKey: 'process_details_page.claim_banner.created.title',
    descriptionKey: 'process_details_page.claim_banner.created.description'
  },
  WFP_CLAIM_STATUS_ACCEPTED: {
    tone: 'success',
    Icon: ShieldCheckIcon,
    titleKey: 'process_details_page.claim_banner.accepted.title',
    descriptionKey: 'process_details_page.claim_banner.accepted.description'
  },
  WFP_CLAIM_STATUS_RESOLVED: {
    tone: 'success',
    Icon: ShieldCheckIcon,
    titleKey: 'process_details_page.claim_banner.resolved.title',
    descriptionKey: 'process_details_page.claim_banner.resolved.description'
  },
  WFP_CLAIM_STATUS_REJECTED: {
    tone: 'danger',
    Icon: ShieldCrossIcon,
    titleKey: 'process_details_page.claim_banner.rejected.title',
    descriptionKey: 'process_details_page.claim_banner.rejected.description'
  },
  WFP_CLAIM_STATUS_CLOSED: {
    tone: 'neutral',
    Icon: ShieldCrossIcon,
    titleKey: 'process_details_page.claim_banner.closed.title',
    descriptionKey: 'process_details_page.claim_banner.closed.description'
  }
}

export const ClaimBanner = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const { data, isLoading } = useClaimStatus({ return_process_id: id })

  if (isLoading) {
    return (
      <Base.Callout tone="neutral" className="mb-4 flex gap-2">
        <div className="h-5 w-40 animate-pulse rounded-sm bg-neutral-200" />
        <div className="h-5 flex-1 animate-pulse rounded-sm bg-neutral-200" />
      </Base.Callout>
    )
  }

  const status = data?.claim?.status ?? 'WFP_CLAIM_STATUS_PENDING_SUBMISSION'

  const { tone, Icon, titleKey, descriptionKey } =
    STATUS_CONFIG[status as PbWfpClaimStatusEnum]

  return (
    <Base.Callout tone={tone} icon={<Icon />} className="mb-4">
      <p>
        <strong className="font-medium">{t(titleKey)}:</strong>{' '}
        {t(descriptionKey)}
      </p>
    </Base.Callout>
  )
}
